import { useCallback, useEffect, useRef, useState } from 'react';
import { GameShell, useGameClock, useReducedMotion } from './GameShell.js';

interface Star {
  id: number;
  value: number;
  x: number;
  y: number;
  speed: number;
  picked: boolean;
}

/**
 * Stars fall; tap the two that make ten.
 *
 * The one a tired five-year-old cannot be bad at. No steering, no aiming, no
 * timing skill - one tap each, and a wrong pair just un-picks itself. It
 * exists so the breather after three misses is genuinely a breather.
 */
export function StarPopper({
  makes,
  pairs,
  seconds,
  onFinish,
}: {
  makes: number;
  pairs: [number, number][];
  seconds: number;
  onFinish: (score: number) => void;
}) {
  const reduced = useReducedMotion();
  const [stars, setStars] = useState<Star[]>([]);
  const [popped, setPopped] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const nextId = useRef(0);
  const queue = useRef<number[]>([]);

  // Seeded from the server's pairs, flattened and shuffled by arrival time so
  // a matching partner is always somewhere on screen or just behind.
  useEffect(() => {
    queue.current = pairs.flatMap(([a, b]) => [a, b]);
  }, [pairs]);

  const finish = useCallback(() => onFinish(popped), [onFinish, popped]);
  const left = useGameClock(seconds, finish);

  useEffect(() => {
    const frame = setInterval(
      () => {
        setStars((current) => {
          let next = current
            .map((star) => ({ ...star, y: star.y + star.speed }))
            .filter((star) => star.y < 100 && !star.picked);

          // Keep a handful in the sky. Running dry is the only way this stops
          // being playable, so the queue loops rather than ending.
          while (next.length < 5 && queue.current.length > 0) {
            const value = queue.current.shift() as number;
            queue.current.push(value);
            next = [
              ...next,
              {
                id: nextId.current++,
                value,
                x: 8 + Math.random() * 78,
                y: -8 - Math.random() * 20,
                speed: reduced ? 0.5 : 0.9 + Math.random() * 0.5,
                picked: false,
              },
            ];
          }
          return next;
        });
      },
      reduced ? 120 : 60,
    );

    return () => clearInterval(frame);
  }, [reduced]);

  const tap = (star: Star) => {
    if (picked === null) {
      setPicked(star.id);
      return;
    }
    if (picked === star.id) {
      setPicked(null);
      return;
    }

    const first = stars.find((s) => s.id === picked);
    if (!first) {
      setPicked(star.id);
      return;
    }

    if (first.value + star.value === makes) {
      setStars((current) => current.filter((s) => s.id !== star.id && s.id !== first.id));
      setPopped((n) => n + 1);
    }
    // A wrong pair costs nothing but the tap. No buzzer, no penalty.
    setPicked(null);
  };

  return (
    <GameShell
      name="Star Popper"
      scoreLabel="Popped"
      score={popped}
      secondsLeft={left}
      totalSeconds={seconds}
      onQuit={finish}
    >
      <div className="popper" aria-label={`Tap two stars that make ${makes}`}>
        <p className="popper__goal">make {makes}</p>
        {stars.map((star) => (
          <button
            key={star.id}
            type="button"
            className="popper__star"
            data-picked={picked === star.id}
            style={{ left: `${star.x}%`, top: `${star.y}%` }}
            onClick={() => tap(star)}
          >
            {star.value}
          </button>
        ))}
        <div className="popper__ground" />
      </div>
    </GameShell>
  );
}
